import React, { useState, useEffect } from "react";
import { apiService } from "../../services/api";
import { PencilIcon } from "@heroicons/react/24/outline";

interface MachineLimitCardProps {
  onEdit?: () => void;
  showEditButton?: boolean;
}

export default function MachineLimitCard({
  onEdit,
  showEditButton = false,
}: MachineLimitCardProps) {
  const [machineStats, setMachineStats] = useState<{
    active_count: number;
    max_machines: number | null;
    usage_percent: number | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMachineStats();
  }, []);

  const loadMachineStats = async () => {
    try {
      setLoading(true);
      // Usar getCompanySettings que retorna dados do tenant incluindo max_machines
      const settingsResponse = await apiService.getCompanySettings();

      if (settingsResponse.success && settingsResponse.data) {
        const tenant = settingsResponse.data;
        const active_count = tenant.active_machines_count || 0;
        const max_machines = tenant.max_machines ?? null;
        const usage_percent =
          max_machines && max_machines > 0
            ? Math.round((active_count / max_machines) * 100 * 10) / 10
            : null;

        setMachineStats({
          active_count,
          max_machines,
          usage_percent,
        });
      }
    } catch (error) {
      console.error("Error loading machine stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 animate-pulse">
        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
      </div>
    );
  }

  if (!machineStats) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Carregando informações de limite de máquinas...
        </div>
      </div>
    );
  }

  const { active_count, max_machines, usage_percent } = machineStats;
  const isUnlimited = max_machines === null || max_machines === undefined;
  const remaining = isUnlimited ? null : max_machines - active_count;
  const isNearLimit =
    !isUnlimited && usage_percent !== null && usage_percent >= 80;
  const isAtLimit =
    !isUnlimited && usage_percent !== null && usage_percent >= 100;

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">
          Limite de Máquinas
        </h3>
        {showEditButton && onEdit && (
          <button
            onClick={onEdit}
            className="inline-flex items-center px-2.5 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PencilIcon className="h-3.5 w-3.5 mr-1" />
            Editar
          </button>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-gray-600 dark:text-gray-400">
            Máquinas Ativas
          </span>
          <span
            className={`text-base font-bold ${
              isAtLimit
                ? "text-red-600 dark:text-red-400"
                : isNearLimit
                ? "text-yellow-600 dark:text-yellow-400"
                : "text-gray-900 dark:text-white"
            }`}
          >
            {active_count}
          </span>
        </div>

        {!isUnlimited && (
          <>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-gray-600 dark:text-gray-400">
                Limite Máximo
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {max_machines}
              </span>
            </div>

            {remaining !== null && (
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  Disponíveis
                </span>
                <span
                  className={`text-sm font-semibold ${
                    remaining <= 0
                      ? "text-red-600 dark:text-red-400"
                      : remaining <= 5
                      ? "text-yellow-600 dark:text-yellow-400"
                      : "text-green-600 dark:text-green-400"
                  }`}
                >
                  {remaining}
                </span>
              </div>
            )}
          </>
        )}

        {/* Sempre mostrar a barra de progresso */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Uso
            </span>
            {!isUnlimited && usage_percent !== null ? (
              <span
                className={`text-xs font-medium ${
                  isAtLimit
                    ? "text-red-600 dark:text-red-400"
                    : isNearLimit
                    ? "text-yellow-600 dark:text-yellow-400"
                    : "text-gray-600 dark:text-gray-400"
                }`}
              >
                {usage_percent.toFixed(1)}%
              </span>
            ) : (
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Ilimitado
              </span>
            )}
          </div>
          <div className="flex items-center space-x-1.5">
            <div className="flex-1 bg-gray-200 rounded-full h-1.5 dark:bg-gray-700">
              {!isUnlimited && usage_percent !== null ? (
                <div
                  className={`h-1.5 rounded-full transition-all ${
                    isAtLimit
                      ? "bg-red-500 dark:bg-red-500"
                      : isNearLimit
                      ? "bg-yellow-500 dark:bg-yellow-500"
                      : "bg-green-500 dark:bg-green-500"
                  }`}
                  style={{ width: `${Math.min(usage_percent, 100)}%` }}
                />
              ) : (
                <div
                  className="h-1.5 rounded-full bg-green-500 dark:bg-green-500"
                  style={{ width: "100%" }}
                />
              )}
            </div>
          </div>
        </div>

        {isUnlimited && (
          <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 italic">
            Sem limite de máquinas configurado
          </div>
        )}

        {isAtLimit && !isUnlimited && (
          <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
            <p className="text-xs text-red-800 dark:text-red-200">
              ⚠️ Limite de máquinas atingido. Não é possível adicionar mais
              máquinas.
            </p>
          </div>
        )}

        {isNearLimit && !isAtLimit && !isUnlimited && (
          <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              ⚠️ Limite de máquinas próximo. Restam apenas {remaining} máquina
              {remaining !== 1 ? "s" : ""}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
